import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  OutlinedInput
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Apps,
  ContentCopy,
  Refresh,
  PhoneAndroid,
  ConnectedTv,
  Language as WebIcon
} from '@mui/icons-material';
import { applicationsAPI, playlistsAPI, radioAPI, tvGuideAPI } from '../services/api';

// Доступные рекламные стратегии — раздельно для мобильных и ТВ
const AVAILABLE_ADS_STRATEGIES_MOBILE = [
  { value: 'interstitial', label: 'interstitial' },
  { value: 'preroll', label: 'preroll' },
  { value: 'interstitial_native', label: 'interstitial_native' }
];

const AVAILABLE_ADS_STRATEGIES_TV = [
  { value: 'interstitial', label: 'interstitial' },
  { value: 'preroll', label: 'preroll' },
  { value: 'interstitial_native', label: 'interstitial_native' }
];

// Баннерные стратегии — какие форматы использовать в баннерном слоте
const AVAILABLE_ADS_STRATEGIES_BANNER_MOBILE = [
  { value: 'banners', label: 'banners' },
  { value: 'native', label: 'native' }
];

const AVAILABLE_ADS_STRATEGIES_BANNER_TV = [
  { value: 'banners', label: 'banners' },
  { value: 'native_tv', label: 'native_tv' }
];

// Значения, сохранённые ранее, но уже убранные из списка, показываем как устаревшие —
// иначе их невозможно снять в мультиселекте
const withLegacyOptions = (options, selected) => {
  const known = new Set(options.map((o) => o.value));
  const legacy = (selected || [])
    .filter((v) => !known.has(v))
    .map((v) => ({ value: v, label: `${v} (устаревшее)` }));
  return [...options, ...legacy];
};

const ApplicationEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    bundle: '',
    api_key: '',
    description: '',
    market_url: '',
    playlist_id: null,
    radio_playlist_id: null,
    tv_guide_source_id: null,
    is_active: true,
    showcase_enabled: true,
    player_type_default: 'frame',
    player_type_default_tv: 'stream',
    player_type_default_web: 'frame',
    grade_timeout: 10,
    grade_url: '',
    ads_strategy: ['interstitial', 'preroll', 'interstitial_native'],
    ads_strategy_tv: ['interstitial', 'preroll', 'interstitial_native'],
    ads_strategy_banner: ['banners'],
    ads_strategy_banner_tv: ['banners']
  });

  const [playlists, setPlaylists] = useState([]);
  const [radioPlaylists, setRadioPlaylists] = useState([]);
  const [tvGuideSources, setTvGuideSources] = useState([]);

  useEffect(() => {
    loadPlaylists();
    loadRadioPlaylists();
    loadTvGuideSources();
    if (!isNew) {
      loadApplication();
    }
  }, [id, isNew]);

  const loadPlaylists = async () => {
    try {
      const response = await playlistsAPI.getAll();
      setPlaylists(response.data);
    } catch (error) {
      console.error('Ошибка загрузки плейлистов:', error);
    }
  };

  const loadRadioPlaylists = async () => {
    try {
      const response = await radioAPI.getPlaylists();
      setRadioPlaylists(response.data);
    } catch (error) {
      console.error('Ошибка загрузки радио-плейлистов:', error);
    }
  };

  const loadTvGuideSources = async () => {
    try {
      const response = await tvGuideAPI.getSources();
      setTvGuideSources(response.data);
    } catch (error) {
      console.error('Ошибка загрузки источников телепрограммы:', error);
    }
  };

  const loadApplication = async () => {
    try {
      setLoading(true);
      const response = await applicationsAPI.getById(id);
      setFormData(response.data);
    } catch (error) {
      console.error('Ошибка загрузки приложения:', error);
      setError('Ошибка загрузки приложения');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateApiKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    handleInputChange('api_key', result);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setSuccess(`${type} скопирован в буфер обмена`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleAdsStrategyChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      ads_strategy: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleAdsStrategyTvChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      ads_strategy_tv: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleAdsStrategyBannerChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      ads_strategy_banner: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleAdsStrategyBannerTvChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      ads_strategy_banner_tv: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!formData.name.trim()) {
        setError('Название приложения обязательно');
        return;
      }
      if (!formData.bundle.trim()) {
        setError('Bundle ID обязателен');
        return;
      }
      if (!formData.api_key.trim()) {
        setError('API ключ обязателен');
        return;
      }

      if (isNew) {
        await applicationsAPI.create(formData);
        setSuccess('Приложение создано успешно');
      } else {
        await applicationsAPI.update(id, formData);
        setSuccess('Приложение обновлено успешно');
      }

      setTimeout(() => {
        navigate('/applications');
      }, 1500);
    } catch (error) {
      console.error('Ошибка сохранения приложения:', error);
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Ошибка сохранения приложения');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/applications');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Заголовок с кнопкой назад */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Apps color="primary" />
          {isNew ? 'Создание приложения' : 'Редактирование приложения'}
        </Typography>
      </Box>

      {/* Сообщения об ошибках и успехе */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Основные настройки */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Основные настройки
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Название приложения"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bundle ID"
                    value={formData.bundle}
                    onChange={(e) => handleInputChange('bundle', e.target.value)}
                    placeholder="com.example.app"
                    required
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Копировать Bundle ID">
                          <IconButton
                            onClick={() => copyToClipboard(formData.bundle, 'Bundle ID')}
                            disabled={!formData.bundle}
                            size="small"
                          >
                            <ContentCopy />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="API ключ"
                    value={formData.api_key}
                    onChange={(e) => handleInputChange('api_key', e.target.value)}
                    required
                    InputProps={{
                      endAdornment: (
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="Сгенерировать новый ключ">
                            <IconButton onClick={generateApiKey} size="small">
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Копировать API ключ">
                            <IconButton
                              onClick={() => copyToClipboard(formData.api_key, 'API ключ')}
                              disabled={!formData.api_key}
                              size="small"
                            >
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Описание"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    multiline
                    rows={3}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ссылка на маркет приложений"
                    value={formData.market_url}
                    onChange={(e) => handleInputChange('market_url', e.target.value)}
                    placeholder="https://play.google.com/store/apps/details?id=..."
                    helperText="Ссылка на Google Play Store, App Store или другой маркет приложений"
                  />
                </Grid>

                {/* Настройки оценки приложения */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Настройки оценки приложения
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Таймаут для показа оценки (сек)"
                    value={formData.grade_timeout}
                    onChange={(e) => handleInputChange('grade_timeout', parseInt(e.target.value) || 10)}
                    helperText="Время до показа предложения оценить приложение (по умолчанию 10 сек)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="URL для оценки (опционально)"
                    value={formData.grade_url}
                    onChange={(e) => handleInputChange('grade_url', e.target.value)}
                    placeholder="Если пусто, используется market_url"
                    helperText="Оставьте пустым для автоматического использования market_url"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>ТВ Плейлист</InputLabel>
                    <Select
                      value={formData.playlist_id || ''}
                      onChange={(e) => handleInputChange('playlist_id', e.target.value || null)}
                      label="ТВ Плейлист"
                    >
                      <MenuItem value="">
                        <em>Плейлист по умолчанию</em>
                      </MenuItem>
                      {playlists.map((playlist) => (
                        <MenuItem key={playlist.id} value={playlist.id}>
                          {playlist.name}
                          {playlist.is_default && ' (по умолчанию)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Радио Плейлист</InputLabel>
                    <Select
                      value={formData.radio_playlist_id || ''}
                      onChange={(e) => handleInputChange('radio_playlist_id', e.target.value || null)}
                      label="Радио Плейлист"
                    >
                      <MenuItem value="">
                        <em>Плейлист по умолчанию</em>
                      </MenuItem>
                      {radioPlaylists.map((playlist) => (
                        <MenuItem key={playlist.id} value={playlist.id}>
                          {playlist.name}
                          {playlist.is_default && ' (по умолчанию)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Источник телепрограммы</InputLabel>
                    <Select
                      value={formData.tv_guide_source_id || ''}
                      onChange={(e) => handleInputChange('tv_guide_source_id', e.target.value || null)}
                      label="Источник телепрограммы"
                    >
                      <MenuItem value="">
                        <em>По приоритету (резервные)</em>
                      </MenuItem>
                      {tvGuideSources.map((source) => (
                        <MenuItem key={source.id} value={source.id}>
                          {source.name}
                          {!source.is_active && ' (неактивен)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      />
                    }
                    label="Приложение активно"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showcase_enabled ?? true}
                        onChange={(e) => handleInputChange('showcase_enabled', e.target.checked)}
                      />
                    }
                    label="Витрина включена"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Настройки плеера */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Настройки плеера по умолчанию
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Тип плеера для мобильных устройств</InputLabel>
                    <Select
                      value={formData.player_type_default}
                      label="Тип плеера для мобильных устройств"
                      onChange={(e) => handleInputChange('player_type_default', e.target.value)}
                      startAdornment={<PhoneAndroid sx={{ mr: 1, color: 'action.active' }} />}
                    >
                      <MenuItem value="frame">Frame (встроенный плеер)</MenuItem>
                      <MenuItem value="stream">Stream (прямой поток)</MenuItem>
                      <MenuItem value="redirect">Redirect (перенаправление)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Тип плеера для ТВ устройств</InputLabel>
                    <Select
                      value={formData.player_type_default_tv}
                      label="Тип плеера для ТВ устройств"
                      onChange={(e) => handleInputChange('player_type_default_tv', e.target.value)}
                      startAdornment={<ConnectedTv sx={{ mr: 1, color: 'action.active' }} />}
                    >
                      <MenuItem value="stream">Stream (прямой поток)</MenuItem>
                      <MenuItem value="frame">Frame (встроенный плеер)</MenuItem>
                      <MenuItem value="redirect">Redirect (перенаправление)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Тип плеера для WEB</InputLabel>
                    <Select
                      value={formData.player_type_default_web || 'frame'}
                      label="Тип плеера для WEB"
                      onChange={(e) => handleInputChange('player_type_default_web', e.target.value)}
                      startAdornment={<WebIcon sx={{ mr: 1, color: 'action.active' }} />}
                    >
                      <MenuItem value="frame">Frame (встроенный плеер)</MenuItem>
                      <MenuItem value="stream">Stream (прямой поток)</MenuItem>
                      <MenuItem value="redirect">Redirect (перенаправление)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Frame:</strong> Встроенный плеер (iframe) - подходит для мобильных устройств<br/>
                  <strong>Stream:</strong> Прямая ссылка на поток - оптимально для ТВ приставок<br/>
                  <strong>Redirect:</strong> Перенаправление на внешний плеер
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Настройки рекламной стратегии */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Рекламная стратегия
              </Typography>
              
              <Grid container spacing={3}>
                {/* Рекламная стратегия для мобильных */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Рекламная стратегия (мобильные)</InputLabel>
                    <Select
                      multiple
                      value={formData.ads_strategy || []}
                      onChange={handleAdsStrategyChange}
                      input={<OutlinedInput label="Рекламная стратегия (мобильные)" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {withLegacyOptions(AVAILABLE_ADS_STRATEGIES_MOBILE, formData.ads_strategy).map((strategy) => (
                        <MenuItem key={strategy.value} value={strategy.value}>
                          {strategy.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Рекламная стратегия для ТВ */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Рекламная стратегия (ТВ)</InputLabel>
                    <Select
                      multiple
                      value={formData.ads_strategy_tv || []}
                      onChange={handleAdsStrategyTvChange}
                      input={<OutlinedInput label="Рекламная стратегия (ТВ)" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {withLegacyOptions(AVAILABLE_ADS_STRATEGIES_TV, formData.ads_strategy_tv).map((strategy) => (
                        <MenuItem key={strategy.value} value={strategy.value}>
                          {strategy.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Баннерная стратегия для мобильных */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Баннерная стратегия (мобильные)</InputLabel>
                    <Select
                      multiple
                      value={formData.ads_strategy_banner || []}
                      onChange={handleAdsStrategyBannerChange}
                      input={<OutlinedInput label="Баннерная стратегия (мобильные)" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {withLegacyOptions(AVAILABLE_ADS_STRATEGIES_BANNER_MOBILE, formData.ads_strategy_banner).map((strategy) => (
                        <MenuItem key={strategy.value} value={strategy.value}>
                          {strategy.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Баннерная стратегия для ТВ */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Баннерная стратегия (ТВ)</InputLabel>
                    <Select
                      multiple
                      value={formData.ads_strategy_banner_tv || []}
                      onChange={handleAdsStrategyBannerTvChange}
                      input={<OutlinedInput label="Баннерная стратегия (ТВ)" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {withLegacyOptions(AVAILABLE_ADS_STRATEGIES_BANNER_TV, formData.ads_strategy_banner_tv).map((strategy) => (
                        <MenuItem key={strategy.value} value={strategy.value}>
                          {strategy.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Рекламная стратегия определяет, какие типы рекламы будут показываться в приложении.
                  Мобильные: interstitial, preroll, interstitial_native. ТВ: interstitial, preroll, interstitial_native.
                  Баннерная стратегия задаёт форматы баннерного слота — мобильные: banners, native; ТВ: banners, native_tv.
                  Список ads_banners в API сохраняется для старых версий приложений.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Кнопки управления */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Сохранение...' : (isNew ? 'Создать приложение' : 'Сохранить изменения')}
        </Button>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={saving}
        >
          Отмена
        </Button>
      </Box>
    </Box>
  );
};

export default ApplicationEditPage;
